<template>
  <div>
    <div style="margin-bottom: 20px;">
      <Button
        :label="t('Adicionar Prioridade')"
        icon="pi pi-plus"
        size="small"
        data-testid="so-priorities-add-btn"
        @click="handleNew"
      />
    </div>

    <!-- SOPriorities Table -->
    <s-o-priorities-table
      ref="soPrioritiesTableRef"
      @edit="handleEdit"
      @delete="handleDelete">
    </s-o-priorities-table>

    <!-- SOPrioritiesModal is centralized in ModalManager.vue - opened via eventBus -->
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import SOPrioritiesTable from './SOPrioritiesTable.vue';
// SOPrioritiesModal is centralized in ModalManager.vue
import { log } from '../../utils/DebugLogger';

type Id = number | string;

interface DataTableInstance {
  loadData: () => void;
}

interface Props {
  translations?: Record<string, unknown>;
}

const props = withDefaults(defineProps<Props>(), {
  translations: () => ({}),
});

// Get i18n composable
const { t } = useI18n();

// Reactive data
const soPrioritiesTableRef = ref<DataTableInstance | null>(null);

// Methods
const loadData = async (): Promise<void> => {
  log('SOPrioritiesPage', 'loadData', 'Reloading data');
  if (soPrioritiesTableRef.value) {
    await soPrioritiesTableRef.value.loadData();
  }
};

const handleNew = (): void => {
  log('SOPrioritiesPage', 'handleNew', 'Opening modal for new priority via eventBus');
  eventBus.emit('modal.so-priorities.open', { action: 'create' });
};

const handleEdit = (id: Id): void => {
  log('SOPrioritiesPage', 'handleEdit', 'Editing priority ID via eventBus', id);
  eventBus.emit('modal.so-priorities.open', { id, action: 'edit' });
};

const handleDelete = (id: Id, data: Record<string, unknown> | null = null): void => {
  log('SOPrioritiesPage', 'handleDelete', 'Deleting priority ID via eventBus', id);
  eventBus.emit('modal.so-priorities.open', { id, data, action: 'delete' });
};

const handleNewEvent = (): void => handleNew();
const handleEditEvent = (id?: Id): void => { if (id !== undefined) handleEdit(id); };
const handleDeleteEvent = (id?: Id): void => { if (id !== undefined) handleDelete(id); };

// Lifecycle
onMounted(() => {
  log('SOPrioritiesPage', 'mounted', 'Component mounted');

  // Set up eventBus listeners for backward compatibility
  eventBus.on('so-priority.new', handleNewEvent);
  eventBus.on('so-priority.edit', handleEditEvent);
  eventBus.on('so-priority.delete', handleDeleteEvent);
});

onBeforeUnmount(() => {
  log('SOPrioritiesPage', 'beforeUnmount', 'Cleaning up event listeners');

  // Clean up eventBus listeners
  eventBus.off('so-priority.new', handleNewEvent);
  eventBus.off('so-priority.edit', handleEditEvent);
  eventBus.off('so-priority.delete', handleDeleteEvent);
});

// Expose methods for parent components
defineExpose({
  loadData,
  handleNew,
  handleEdit,
  handleDelete
});
</script>

<style scoped>
</style>
